import { EditorContent, Extensions, useEditor } from '@tiptap/react';
import { htmlToMarkdown, markdownToSanitizedHTML } from '@ardoq/markdown';
import {
  Toolbar,
  ToolbarLabels,
  defaultToolbarLabels,
} from './toolbar/Toolbar';
import {
  defaultExtensions,
  ExtensionLabels,
  imageNodeWithoutPicker,
  RichTextEditorExtension,
  parseRichTextEditorExtension,
} from './extensions/extensionConfig';
import { defaultLinkFormLabels, LinkFormLabels } from './toolbar/LinkForm';
import {
  defaultLinkBubbleMenuLabels,
  LinkBubbleMenuLabels,
} from './extensions/LinkBubleMenu';
import {
  defaultTableNodeViewLabels,
  TableNodeViewLabels,
} from './extensions/TableNodeView';
import {
  defaultPresentationDialogLabels,
  PresentationDialogLabels,
} from './extensions/PresentationSelectorDialog';
import { Field } from '@ardoq/forms';
import styled, { css } from 'styled-components';
import { colors, radius } from '@ardoq/design-tokens';
import { errorCSS, warningCSS } from '@ardoq/style-helpers';
import { PopoverContentType } from '@ardoq/popovers';
import {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Focus from '@tiptap/extension-focus';
import GapCursor from '@tiptap/extension-gapcursor';
import HardBreak from '@tiptap/extension-hard-break';
import { TextStyle } from '@tiptap/extension-text-style';
import Prompt from '@tiptap/extension-placeholder';
import { uniqBy } from 'lodash';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { useResizeObserver } from '@ardoq/hooks';
import History from '@tiptap/extension-history';
import { PlaceholderExtensionItems } from './extensions/PlaceholderExtension';
import { Selection } from './extensions/SelectionExtension';
import { LinkBubbleMenu } from './extensions/LinkBubleMenu';
import {
  defaultImageBubbleMenuLabels,
  ImageBubbleMenu,
  ImageBubbleMenuLabels,
} from './extensions/ImageBubbleMenu';
import {
  GenerateDescriptionButton,
  GenerateDescriptionButtonLabels,
  GenerateDescriptionProps,
} from './toolbar/GenerateDescriptionButton';
import { GenerateDescriptionToolbarButton } from './toolbar/GenerateDescriptionToolbarButton';
import {
  ContentSync,
  createContentSync,
  recordEmission,
  resolveIncomingContent,
} from './contentSync';

export type RichTextEditorLabels = {
  toolbar?: ToolbarLabels;
  linkForm?: LinkFormLabels;
  linkBubbleMenu?: LinkBubbleMenuLabels;
  imageBubbleMenu?: ImageBubbleMenuLabels;
  table?: TableNodeViewLabels;
  tableMenuButton?: string;
  presentationDialog?: PresentationDialogLabels;
  generateDescription?: GenerateDescriptionButtonLabels;
};

export const defaultRichTextEditorLabels: Required<RichTextEditorLabels> = {
  toolbar: defaultToolbarLabels,
  linkForm: defaultLinkFormLabels,
  linkBubbleMenu: defaultLinkBubbleMenuLabels,
  imageBubbleMenu: defaultImageBubbleMenuLabels,
  table: defaultTableNodeViewLabels,
  tableMenuButton: 'Menu',
  presentationDialog: defaultPresentationDialogLabels,
  generateDescription: {
    label: 'Generate description',
    tooltip: 'Generate description',
  },
};

export interface RichTextEditorProps {
  readonly onInputChange: (markdownString: string) => void;
  content?: string;
  toolbarExtensions?: RichTextEditorExtension[];
  errorMessage?: string;
  warningMessage?: string;
  label?: string;
  helperText?: string;
  dataTestId?: string;
  popoverContent?: PopoverContentType;
  popoverHelpContent?: ReactNode;
  isDisabled?: boolean;
  indicator?: 'optional' | 'required';
  isRequired?: boolean;
  className?: string;
  onBlur?: VoidFunction;
  onCommit?: (markdownString: string) => void;
  onFocus?: VoidFunction;
  minHeight?: string;
  autoFocus?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  generateDescriptionProps?: GenerateDescriptionProps;
  /**
   * Rendered inside the editor's content area, above the text. For status that
   * belongs to the content rather than the surrounding form — an AI generation
   * in progress, say — so the editor stays visible and in place instead of
   * being swapped out for a placeholder.
   */
  inlineStatus?: ReactNode;
  labels?: RichTextEditorLabels;

  // Needed to support selecting files and attachments from the workspace
  workspaceId?: string;
}

const Border = styled.div<{
  $hasError: boolean;
  $hasWarning: boolean;
  $isDisabled: boolean;
}>`
  display: flex;
  flex-direction: column;
  border-radius: ${radius.medium};
  z-index: 2;
  overflow: hidden;
  box-shadow: 0px 0px 0px 1px ${colors.borderAction};
  ${({ $isDisabled }) =>
    !$isDisabled &&
    css`
      &:hover {
        box-shadow: 0px 0px 0px 1px ${colors.borderActionHover};
      }
    `}
  ${({ $hasError }) => $hasError && errorCSS}
  ${({ $hasError, $hasWarning }) => !$hasError && $hasWarning && warningCSS}
  &:has(.ProseMirror-focused) {
    box-shadow: 0px 0px 0px 2px ${colors.borderActionActive};
  }
`;

const StyledEditorContent = styled(EditorContent)`
  /* Fixes an issue where the background would be part of the
     drag preview when dragging the image */
  .node-image {
    width: fit-content;
  }
`;

/**
 * Removes trailing newlines before closing code tags to maintain
 * compatibility with the old editor's rendering behavior.
 */
const replaceTrailingNewlineInCodeElements = (html: string) => {
  return html.replace(/\n<\/code>/g, '</code>');
};

const convertToEditorContent = (
  content?: string,
  placeholderExtensionValues: string[] = []
) => {
  const sanitizedHtml = markdownToSanitizedHTML(content, 'permissive');

  if (placeholderExtensionValues.length > 0) {
    return placeholderExtensionValues.reduce((acc, placeholder) => {
      return acc.replaceAll(
        placeholder,
        `<span data-type="placeholder">${placeholder}</span>`
      );
    }, sanitizedHtml);
  }

  return replaceTrailingNewlineInCodeElements(sanitizedHtml);
};

export const RichTextEditor = ({
  label,
  helperText,
  dataTestId,
  popoverContent,
  popoverHelpContent,
  isDisabled,
  indicator,
  errorMessage,
  warningMessage,
  content,
  toolbarExtensions = defaultExtensions,
  onInputChange,
  onBlur,
  onCommit,
  onFocus,
  autoFocus = false,
  minHeight = '300px',
  className = '',
  placeholder,
  isRequired,
  ariaLabel,
  generateDescriptionProps,
  inlineStatus,
  labels,
  workspaceId,
}: RichTextEditorProps) => {
  const labelId = useId();

  const resolvedLabels: Required<RichTextEditorLabels> = {
    toolbar: {
      ...defaultRichTextEditorLabels.toolbar,
      ...labels?.toolbar,
      items: {
        ...defaultRichTextEditorLabels.toolbar.items,
        ...labels?.toolbar?.items,
      },
    },
    linkForm: {
      ...defaultRichTextEditorLabels.linkForm,
      ...labels?.linkForm,
    },
    linkBubbleMenu: {
      ...defaultRichTextEditorLabels.linkBubbleMenu,
      ...labels?.linkBubbleMenu,
    },
    imageBubbleMenu: {
      ...defaultRichTextEditorLabels.imageBubbleMenu,
      ...labels?.imageBubbleMenu,
    },
    table: {
      ...defaultRichTextEditorLabels.table,
      ...labels?.table,
    },
    tableMenuButton:
      labels?.tableMenuButton ?? defaultRichTextEditorLabels.tableMenuButton,
    presentationDialog: {
      ...defaultRichTextEditorLabels.presentationDialog,
      ...labels?.presentationDialog,
    },
    generateDescription: {
      ...defaultRichTextEditorLabels.generateDescription,
      ...labels?.generateDescription,
    },
  };

  const extensionLabels: ExtensionLabels = {
    table: resolvedLabels.table,
    tableMenuButton: resolvedLabels.tableMenuButton,
    presentation: resolvedLabels.presentationDialog,
  };

  let parsedExtensions: Extensions = uniqBy(
    [
      ...toolbarExtensions.flatMap(extensions =>
        parseRichTextEditorExtension(extensions, workspaceId, extensionLabels)
      ),
      imageNodeWithoutPicker,
    ],
    'name'
  );

  const placeholderExtensionValues = parsedExtensions
    .filter(extension => extension.name === 'placeholderDropdown')
    .flatMap(extension =>
      extension.options.items.map(
        (item: PlaceholderExtensionItems) => item.value
      )
    );

  if (placeholder) {
    parsedExtensions.push(
      Prompt.configure({
        placeholder: placeholder,
      })
    );
  }

  // These are the base extensions necessary for the editor to work
  parsedExtensions = [
    Text,
    TextStyle,
    Document,
    GapCursor,
    HardBreak,
    Focus.configure({}),
    Paragraph,
    History,
    Selection,
    ...parsedExtensions,
  ];

  const [shouldPulse, setShouldPulse] = useState(false);
  const triggerGenerateDescriptionButtonPulse = useCallback(() => {
    setShouldPulse(true);
    setTimeout(() => {
      setShouldPulse(false);
    }, 5000);
  }, []);

  const contentSyncRef = useRef<ContentSync>(createContentSync(content ?? ''));
  const fieldRef = useRef<HTMLDivElement>(null);
  const { width } = useResizeObserver(fieldRef);

  const isCommitedRef = useRef(false);

  const handleBlur = () => {
    onBlur?.();
    if (isCommitedRef.current && onCommit) {
      onCommit(contentSyncRef.current.editorContent);
      isCommitedRef.current = false;
    }
  };

  const editor = useEditor(
    {
      content: convertToEditorContent(content, placeholderExtensionValues),
      editable: !isDisabled,
      autofocus: autoFocus,
      extensions: parsedExtensions,
      onBlur: handleBlur,
      onFocus,
      onUpdate: ({ editor }) => {
        if (!content || content.length === 0) {
          triggerGenerateDescriptionButtonPulse();
        }
        isCommitedRef.current = true;
        const html = editor.getHTML();
        const markdownString = htmlToMarkdown(html);
        contentSyncRef.current = recordEmission(
          contentSyncRef.current,
          markdownString
        );
        onInputChange(markdownString);
      },
      editorProps: {
        attributes: {
          role: 'textbox',
          ...(isDisabled ? { 'aria-disabled': 'true' } : {}),
          ...(isRequired ? { 'aria-required': 'true' } : {}),
          class: className,
          ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
          ...(!ariaLabel && { 'aria-labelledby': labelId }),
          style: `min-height: ${minHeight}; overflow: auto;`,
        },
      },
    },
    [isDisabled]
  );

  useEffect(() => {
    if (!editor) return;
    const { shouldApply, sync } = resolveIncomingContent(
      contentSyncRef.current,
      content ?? ''
    );
    contentSyncRef.current = sync;
    if (!shouldApply) return;
    editor.commands.setContent(
      convertToEditorContent(content, placeholderExtensionValues),
      { emitUpdate: false }
    );
    // The placeholder item set is snapshotted into extension storage at editor
    // creation, so it cannot change while this editor lives; depending on the
    // array would replace the content on every unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, editor]);

  return (
    <Field
      labelId={labelId}
      ref={fieldRef}
      errorMessage={errorMessage}
      warningMessage={warningMessage}
      label={label}
      helperText={helperText}
      dataTestId={dataTestId}
      popoverContent={popoverContent}
      popoverHelpContent={popoverHelpContent}
      isDisabled={isDisabled}
      indicator={indicator}
    >
      <Border
        $isDisabled={Boolean(isDisabled)}
        $hasError={Boolean(errorMessage)}
        $hasWarning={Boolean(warningMessage)}
      >
        <Toolbar
          isDisabled={isDisabled}
          editor={editor}
          width={width}
          labels={resolvedLabels.toolbar}
          linkFormLabels={resolvedLabels.linkForm}
          linkTooltipText={resolvedLabels.toolbar.items.link}
          additionalToolbarButtons={
            generateDescriptionProps && content && content.length > 0 ? (
              <GenerateDescriptionToolbarButton
                generateDescriptionTooltip={
                  resolvedLabels.generateDescription.tooltip
                }
                {...generateDescriptionProps}
                isDisabled={isDisabled}
                shouldPulse={shouldPulse}
              />
            ) : undefined
          }
        />
        <Box
          paddingX="medium"
          paddingY="small"
          backgroundColor={isDisabled ? 'surfaceDisabled' : 'surfaceDefault'}
        >
          <Stack gap="small">
            {inlineStatus}
            <StyledEditorContent editor={editor} />
            {generateDescriptionProps && (!content || content.length === 0) && (
              <FlexBox justify="flex-end">
                <GenerateDescriptionButton
                  label={resolvedLabels.generateDescription.label}
                  {...generateDescriptionProps}
                  onClick={() => {
                    generateDescriptionProps.onClick?.();
                    triggerGenerateDescriptionButtonPulse();
                  }}
                  isDisabled={
                    isDisabled ||
                    generateDescriptionProps.isMissingItemToDescribe
                  }
                />
              </FlexBox>
            )}
          </Stack>
        </Box>
        <LinkBubbleMenu
          editor={editor}
          labels={resolvedLabels.linkBubbleMenu}
          linkFormLabels={resolvedLabels.linkForm}
        />
        <ImageBubbleMenu
          editor={editor}
          labels={resolvedLabels.imageBubbleMenu}
        />
      </Border>
    </Field>
  );
};
